<!--
  @component

  The checkbox component

  ```html
  <oc-checkbox-v1 value="otto" name="brand">Otto</oc-checkbox-v1>
  ```

  @slot required `default` **Mandatory for accessibility!**<br />The label for the checkbox element.<br />It provides a brief description of the input field, making it easier for users to understand its purpose.
-->
<svelte:options
  customElement={{
    tag: "oc-checkbox-v1",
    shadow: "none",
    /*                                                         */
    /*         */
    extend: (CustomElementConstructor) =>
      class extends CustomElementConstructor {
        /*                                          */
        static formAssociated = true;

        constructor() {
          super();

          /*                          */
          this.attachShadow({ mode: "open", delegatesFocus: true });

          /*                                */
          this.internals = this.attachInternals();

          this.setValidity = (...args) => this.internals.setValidity(...args);
        }

        /*                                                            */
        get willValidate() {
          return this.internals.willValidate;
        }

        get validity() {
          return this.internals.validity;
        }

        get validationMessage() {
          return this.internals.validationMessage;
        }

        get form() {
          return this.internals.form;
        }

        /*                       */
        formResetCallback() {
          /*                                   */
          this.checked = this.hasAttribute("checked");
        }
      },

    props: {
      name: { type: "String", reflect: true }, /*                                    */
      value: { type: "String" },
      checked: { type: "Boolean" }, /*                                     */
      variant: { type: "String" },
      disabled: { type: "Boolean", reflect: true }, /*                                    */
    },
  }}
/>

<script lang="ts">
  import { usePropertyChange } from "../../../../common/utils/usePropertyChange";

  usePropertyChange();

  /**
 *
 */
  export let name: string;

  /**
 *
 *
 *
 *
 *
 *
 *
 *
 */
  export let value: string = "on";

  /**
 *
 */
  export let checked = false;

  /**
 *
 *
 *
 */
  export let variant: "default" | "error" = "default";

  /**
 *
 *
 *
 */
  export let disabled: boolean = false;

  /*            */
  export let internals: ElementInternals;

  let inputElement: HTMLInputElement;

  $: if (inputElement) {
    internals?.setFormValue(checked ? value : null);
    if (!internals?.form?.noValidate) {
      internals.setValidity(inputElement.validity, inputElement.validationMessage, inputElement);
    }
  }
</script>

<label
  class="checkbox"
  class:checkbox--error={variant === "error"}
  class:checkbox--disabled={disabled}
>
  <input
    class="checkbox__input"
    bind:this={inputElement}
    type="checkbox"
    {value}
    {disabled}
    {name}
    bind:checked
    tabindex="0"
  />
  <slot />
</label>

<style lang="scss">
  @use "./CheckboxV1.config" as config;
  @use "@otto-ec/design-tokens/tokens";

  @use "../../../../common/scss/focus.scss";

  :host {
    @include config.oc-checkbox;
    padding-left: unset !important; /*                           */
  }

  .checkbox {
    box-sizing: border-box;
    position: relative;
    display: block;
    padding-left: config.$label-padding-left;

    color: tokens.$oc-component-form-default-label-color;

    &__input {
      @include focus.styles(tokens.$oc-semantic-focus-outline-radius-50);

      all: unset;

      position: absolute;
      top: 0;
      left: 0;

      width: tokens.$oc-component-form-checkbox-container-size;
      height: tokens.$oc-component-form-checkbox-container-size;

      outline: 1px solid tokens.$oc-component-form-default-border-color;
      border-radius: tokens.$oc-component-form-checkbox-border-radius;

      &:hover:not(:disabled) {
        outline-width: 2px;
      }

      &:active {
        outline-color: tokens.$oc-component-form-default-border-color-active;
      }

      &:focus {
        outline-color: tokens.$oc-component-form-default-border-color-focus;
      }

      &:checked:before {
        $center-icon: calc(50% - tokens.$oc-component-form-checkbox-icon-size/2);

        content: "";
        display: block;
        position: absolute;
        left: $center-icon;
        top: $center-icon;
        bottom: $center-icon;
        right: $center-icon;

        mask-image: url("/assets-static/icons/pl_icon_check50.svg");
        background-color: tokens.$oc-component-form-default-icon-color;
      }
    }

    &--error {
      color: tokens.$oc-component-form-error-label-color;

      .checkbox__input {
        outline-color: tokens.$oc-component-form-error-border-color;

        &:checked:before {
          background-color: tokens.$oc-component-form-error-icon-color;
        }
      }
    }

    &--disabled {
      color: tokens.$oc-component-form-disabled-label-color;

      .checkbox__input {
        outline-color: tokens.$oc-component-form-disabled-border-color;
        background-color: tokens.$oc-component-form-disabled-background-color;

        &:checked:before {
          background-color: tokens.$oc-component-form-disabled-icon-color;
        }
      }
    }
  }
</style>
